import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Seo } from "../lib/seo.js";
import { GAMES, type GameMeta } from "../content/games.js";
import { GameThumb } from "./Games.js";

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/**
 * Casino — public demo lobby with in-place play.
 *
 * Left sidebar holds the games list (vertical stack of compact tiles).
 * Main area is one of two states:
 *   - playingSlug === null → featured-game showcase + trust signals
 *   - playingSlug !== null → iframe of /play/<slug> + "Back to Lobby" button
 *
 * Tiles use the EXACT same `<GameThumb>` SVGs that the /games catalog
 * renders (real production VaultDoor for Vault Breaker, the 5×5 plate grid
 * for Pressure Plate, the 4-tumbler dial for Cipher Crack), scaled via CSS
 * transform so the same 180×180 source renders cleanly at 56px (sidebar)
 * and 280px (featured). No more hand-drawn approximations.
 *
 * The iframe approach reuses existing same-origin headers from vercel.json
 * (X-Frame-Options: SAMEORIGIN + CSP frame-ancestors 'self'). Each game
 * runs its own balance/state/audio inside the iframe; the casino's outer
 * chrome just hosts.
 */
export function Casino() {
  const live = GAMES.filter((g) => g.status === "live");
  const inDev = GAMES.filter((g) => g.status === "in-dev");
  const [hoverSlug, setHoverSlug] = useState<string | null>(null);
  const [featuredSlug, setFeaturedSlug] = useState<string>("cipher-crack");
  const [playingSlug, setPlayingSlug] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 720px)");
  const featured = GAMES.find((g) => g.slug === featuredSlug) ?? live[0]!;
  const playing = playingSlug ? GAMES.find((g) => g.slug === playingSlug) : null;

  return (
    <>
      <Seo
        title="Casino — play demo"
        description="Play Vault Breaker, Pressure Plate, and Cipher Crack in demo mode. Provably-fair crypto casino originals from Vault Breaker Studios."
        path="/casino"
      />

      <div
        style={{
          position: "relative",
          // Desktop locks to viewport (no page-scroll). Mobile drops the lock
          // so the page can scroll vertically — the 260px sidebar squeezes
          // the playable area below ~100px on iPhone otherwise.
          height: isMobile ? "auto" : "100vh",
          minHeight: isMobile ? "100vh" : undefined,
          background: "#0A0E18",
          color: "var(--fg-1)",
          fontFamily: "var(--font-ui)",
          overflow: isMobile ? "visible" : "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: isMobile ? "auto" : "100vh",
            minHeight: isMobile ? "100vh" : undefined,
          }}
        >
          {/* TOP BAR */}
          <header style={topBarStyle}>
            <Link
              to="/"
              aria-label="Vault Breaker Studios — home"
              style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
            >
              <img src="/logo-mark.svg" alt="" style={{ height: 22, width: 22 }} />
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 14,
                  letterSpacing: "0.06em",
                  color: "var(--fg-1)",
                  lineHeight: 1,
                }}
              >
                VAULT BREAKER
                <span style={{ color: "var(--fg-3)", marginLeft: 6, fontSize: 10, letterSpacing: "0.2em" }}>
                  CASINO
                </span>
              </span>
            </Link>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  background: "rgba(11,14,22,0.7)",
                  border: "1px solid var(--line)",
                  borderRadius: 4,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--fg-1)",
                  letterSpacing: 0.5,
                }}
              >
                <span style={{ color: "var(--gold-bright)", fontWeight: 700 }}>BTC</span>
                0.05000000
                <span style={{ color: "var(--fg-3)", fontSize: 10, marginLeft: 4 }}>DEMO</span>
              </div>
              <Link
                to="/contact"
                className="vb-hide-mobile"
                style={{
                  textDecoration: "none",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: "var(--gold-bright)",
                  border: "1px solid rgba(241,203,107,0.45)",
                  borderRadius: 3,
                  padding: "6px 12px",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                For Operators
              </Link>
            </div>
          </header>

          {/* MAIN GRID — left rail (games) + main area. On mobile the rail
              becomes a horizontal scroll strip above the featured panel and
              the page scrolls vertically. */}
          <div style={isMobile ? mobileMainGridStyle : mainGridStyle}>
            {isMobile ? (
              <aside style={mobileRailStyle}>
                <SectionHeader label="Vault Breaker Originals" count={live.length} />
                <div style={mobileTileScrollStyle}>
                  {live.map((g) => (
                    <SidebarTile
                      key={g.slug}
                      g={g}
                      hover={hoverSlug === g.slug}
                      onHover={(s) => setHoverSlug(s)}
                      onPreview={(s) => setFeaturedSlug(s)}
                      onPlay={(s) => setPlayingSlug(s)}
                      isFeatured={featuredSlug === g.slug}
                      isPlaying={playingSlug === g.slug}
                    />
                  ))}
                  {inDev.map((g) => (
                    <SidebarSoonTile key={g.slug} g={g} />
                  ))}
                </div>
              </aside>
            ) : (
              <aside style={leftRailStyle}>
                <div
                  style={{
                    padding: "6px 14px",
                    background: "rgba(241,203,107,0.12)",
                    border: "1px solid var(--gold-bright)",
                    borderRadius: 999,
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    color: "var(--gold-bright)",
                    textTransform: "uppercase",
                    alignSelf: "flex-start",
                    marginBottom: 6,
                  }}
                >
                  Originals
                </div>

                <SectionHeader label="Vault Breaker Originals" count={live.length} />
                <div style={tileColumnStyle}>
                  {live.map((g) => (
                    <SidebarTile
                      key={g.slug}
                      g={g}
                      hover={hoverSlug === g.slug}
                      onHover={(s) => setHoverSlug(s)}
                      onPreview={(s) => setFeaturedSlug(s)}
                      onPlay={(s) => setPlayingSlug(s)}
                      isFeatured={featuredSlug === g.slug}
                      isPlaying={playingSlug === g.slug}
                    />
                  ))}
                </div>

                <SectionHeader label="Coming soon" count={inDev.length} muted />
                <div style={tileColumnStyle}>
                  {inDev.map((g) => (
                    <SidebarSoonTile key={g.slug} g={g} />
                  ))}
                </div>
              </aside>
            )}

            {/* MAIN AREA — showcase OR iframe */}
            <main style={isMobile ? mobileMainAreaStyle : mainAreaStyle}>
              {playing ? (
                <PlayInPlace
                  g={playing}
                  onBack={() => setPlayingSlug(null)}
                />
              ) : (
                <FeaturedShowcase g={featured} onPlay={() => setPlayingSlug(featured.slug)} />
              )}
            </main>
          </div>

          {/* B2B FOOTER STRIP */}
          <footer style={footerStripStyle}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 2.5,
                  color: "var(--fg-3)",
                  textTransform: "uppercase",
                }}
              >
                For crypto casino operators
              </span>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--fg-2)" }}>
                Integrate any of these games in under a week. $0 setup · 10% GGR · USDT/USDC weekly.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to="/integration" style={ghostButton}>
                Integration
              </Link>
              <Link to="/contact" style={goldButton}>
                Contact Sales
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}

// ---- ScaledThumb -------------------------------------------------------
// Mounts the existing 180×180 GameThumb at any visual size via CSS
// transform: scale(). Uses top-left origin + absolute positioning so the
// scaled visual lands cleanly in the top-left of the outer box and
// overflow:hidden clips correctly. (Earlier versions used center origin
// which placed the scaled visual outside the clip rect on small sizes.)

function ScaledThumb({ size, slug }: { size: number; slug: string }) {
  const ratio = size / 180;
  return (
    <div
      style={{
        width: size,
        height: size,
        overflow: "hidden",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 180,
          height: 180,
          transform: `scale(${ratio})`,
          transformOrigin: "top left",
        }}
      >
        <GameThumb slug={slug} />
      </div>
    </div>
  );
}

// ---- Section header (small label + count) ------------------------------

function SectionHeader({ label, count, muted = false }: { label: string; count: number; muted?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 8,
        marginTop: 6,
        marginBottom: 6,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 1.8,
          color: muted ? "var(--fg-4)" : "var(--fg-3)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          color: muted ? "var(--fg-4)" : "var(--fg-3)",
          letterSpacing: 1,
        }}
      >
        {count}
      </span>
    </div>
  );
}

// ---- Sidebar tile (compact horizontal, sits in left column) ------------

function SidebarTile({
  g,
  hover,
  onHover,
  onPreview,
  onPlay,
  isFeatured,
  isPlaying,
}: {
  g: GameMeta;
  hover: boolean;
  onHover: (slug: string | null) => void;
  onPreview: (slug: string) => void;
  onPlay: (slug: string) => void;
  isFeatured: boolean;
  isPlaying: boolean;
}) {
  return (
    <button
      type="button"
      onMouseEnter={() => {
        onHover(g.slug);
        onPreview(g.slug);
      }}
      onMouseLeave={() => onHover(null)}
      onClick={() => onPlay(g.slug)}
      aria-label={`Play ${g.name} demo`}
      style={{
        ...sidebarTileStyle,
        appearance: "none",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        borderColor: isPlaying
          ? "var(--gold-bright)"
          : isFeatured
            ? "rgba(241,203,107,0.55)"
            : hover
              ? "rgba(241,203,107,0.40)"
              : "var(--line)",
        background: isPlaying
          ? "rgba(241,203,107,0.08)"
          : isFeatured
            ? "rgba(241,203,107,0.04)"
            : "var(--bg-2)",
        boxShadow: isPlaying
          ? "0 0 0 1px var(--gold-bright), 0 6px 18px rgba(0,0,0,0.45)"
          : undefined,
      }}
    >
      <div style={sidebarThumbStyle}>
        <ScaledThumb size={56} slug={g.slug} />
        <div style={originalBadgeStyle}>ORIGINAL</div>
        {isPlaying && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(7,9,15,0.55)",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 8,
              fontWeight: 800,
              letterSpacing: 1.5,
              color: "var(--gold-bright)",
              textTransform: "uppercase",
            }}
          >
            Playing
          </div>
        )}
      </div>
      <div style={sidebarMetaStyle}>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 13,
            fontWeight: 700,
            color: "var(--fg-1)",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {g.name}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-3)",
            letterSpacing: 0.5,
            marginTop: 2,
          }}
        >
          {g.rtpPercent}% RTP{g.roundSeconds > 0 ? ` · ${g.roundSeconds}s` : ""}
        </div>
      </div>
    </button>
  );
}

function SidebarSoonTile({ g }: { g: GameMeta }) {
  return (
    <article style={{ ...sidebarTileStyle, opacity: 0.55, cursor: "not-allowed" }}>
      <div style={{ ...sidebarThumbStyle, background: "linear-gradient(180deg, #14192A 0%, #0A0E18 100%)" }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            fontFamily: "var(--font-display)",
            fontSize: 22,
            color: "var(--fg-3)",
            letterSpacing: "0.06em",
            opacity: 0.4,
          }}
        >
          {g.name.split(" ").map((w) => w[0]).join("")}
        </div>
        <div
          style={{
            ...originalBadgeStyle,
            background: "rgba(0,229,255,0.10)",
            borderColor: "rgba(0,229,255,0.35)",
            color: "var(--cyan)",
          }}
        >
          SOON
        </div>
      </div>
      <div style={sidebarMetaStyle}>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 13,
            fontWeight: 700,
            color: "var(--fg-2)",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {g.name}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-4)",
            letterSpacing: 0.8,
            marginTop: 2,
          }}
        >
          IN DEV
        </div>
      </div>
    </article>
  );
}

// ---- Featured showcase (when not playing) ------------------------------

function FeaturedShowcase({ g, onPlay }: { g: GameMeta; onPlay: () => void }) {
  return (
    <section style={featuredWrapStyle}>
      <div style={featuredCoverStyle}>
        <ScaledThumb size={220} slug={g.slug} />
      </div>
      <div style={featuredTextStyle}>
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 2,
            color: "var(--gold-bright)",
            textTransform: "uppercase",
          }}
        >
          Featured · Original
        </span>
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 0.95,
            color: "var(--fg-1)",
            letterSpacing: -0.5,
            textTransform: "uppercase",
          }}
        >
          {g.name}
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: "var(--font-ui)",
            fontSize: 14,
            color: "var(--fg-2)",
            lineHeight: 1.55,
            maxWidth: 460,
          }}
        >
          {g.description}
        </p>
        <div
          style={{
            display: "flex",
            gap: 14,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--fg-3)",
            letterSpacing: 0.5,
            flexWrap: "wrap",
          }}
        >
          {g.roundSeconds > 0 && <span>{g.roundSeconds}s rounds</span>}
          <span>{g.rtpPercent}% RTP</span>
          <span>provably fair</span>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onPlay}
            style={{
              cursor: "pointer",
              padding: "12px 22px",
              background:
                "linear-gradient(180deg, var(--gold-bright) 0%, var(--gold) 100%)",
              border: "1px solid var(--gold-deep)",
              borderRadius: 3,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 1.5,
              color: "var(--gold-shadow)",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Play Demo
          </button>
          <Link
            to={`/games/${g.slug}`}
            style={{
              textDecoration: "none",
              padding: "12px 18px",
              background: "transparent",
              border: "1px solid var(--line)",
              borderRadius: 3,
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 1.5,
              color: "var(--fg-2)",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Math sheet
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---- PlayInPlace — iframe + back button --------------------------------

function PlayInPlace({ g, onBack }: { g: GameMeta; onBack: () => void }) {
  // Vault Breaker (the original crash game) lives at /play; the others
  // each have their own /play/<slug> route.
  const src = g.slug === "vault-breaker" ? "/play" : `/play/${g.slug}`;
  return (
    <div style={iframeWrapStyle}>
      <div style={iframeBarStyle}>
        <button
          type="button"
          onClick={onBack}
          style={{
            cursor: "pointer",
            padding: "6px 14px",
            background: "transparent",
            border: "1px solid var(--line)",
            borderRadius: 3,
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.5,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          ← Back to Lobby
        </button>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--fg-3)",
            letterSpacing: 0.5,
          }}
        >
          Now playing: <span style={{ color: "var(--gold-bright)" }}>{g.name}</span> · demo mode
        </span>
        <Link
          to={`/games/${g.slug}`}
          style={{
            textDecoration: "none",
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--line)",
            borderRadius: 3,
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            color: "var(--fg-3)",
            textTransform: "uppercase",
          }}
        >
          Math ↗
        </Link>
      </div>
      <iframe
        key={g.slug /* re-mount on game switch so state resets */}
        src={src}
        title={`${g.name} demo`}
        style={iframeStyle}
        allow="autoplay; clipboard-read"
      />
    </div>
  );
}

// ---- styles -------------------------------------------------------------

const topBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  height: 42,
  borderBottom: "1px solid var(--line)",
  background: "rgba(7,9,15,0.92)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  flexShrink: 0,
};

const mainGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "260px 1fr",
  gap: 0,
  flex: 1,
  minHeight: 0,
};

const leftRailStyle: CSSProperties = {
  padding: "10px 10px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  background: "rgba(7,9,15,0.6)",
  borderRight: "1px solid var(--line)",
  minHeight: 0,
  overflowY: "auto",
};

const tileColumnStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const sidebarTileStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "76px 1fr",
  gap: 10,
  padding: 8,
  background: "var(--bg-2)",
  border: "1px solid var(--line)",
  borderRadius: 6,
  overflow: "hidden",
  cursor: "pointer",
  transition: "transform 200ms ease, border-color 200ms ease, background 200ms ease",
  alignItems: "center",
  width: "100%",
  minWidth: 220,
  flexShrink: 0,
};

const sidebarThumbStyle: CSSProperties = {
  position: "relative",
  width: 76,
  height: 56,
  background: "linear-gradient(180deg, #0E1118 0%, #07090F 100%)",
  borderRadius: 4,
  overflow: "hidden",
  flexShrink: 0,
  display: "grid",
  placeItems: "center",
};

const sidebarMetaStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const originalBadgeStyle: CSSProperties = {
  position: "absolute",
  top: 4,
  left: 4,
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  padding: "1px 5px",
  background: "rgba(241,203,107,0.10)",
  border: "1px solid rgba(241,203,107,0.35)",
  borderRadius: 2,
  fontFamily: "var(--font-ui)",
  fontSize: 7,
  fontWeight: 800,
  color: "var(--gold-bright)",
  letterSpacing: 0.8,
  textTransform: "uppercase",
  zIndex: 2,
};

const mainAreaStyle: CSSProperties = {
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
};

const featuredWrapStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 20,
  alignItems: "center",
  padding: 18,
  background:
    "radial-gradient(ellipse 70% 90% at 30% 30%, rgba(241,203,107,0.08), transparent 60%), var(--bg-2)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  flex: 1,
  minHeight: 0,
};

const featuredCoverStyle: CSSProperties = {
  width: "100%",
  aspectRatio: "1 / 1",
  maxWidth: 240,
  background: "linear-gradient(180deg, #0E1118 0%, #07090F 100%)",
  border: "1px solid var(--line)",
  borderRadius: 6,
  overflow: "hidden",
  display: "grid",
  placeItems: "center",
};

const featuredTextStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  minWidth: 0,
};

// ---- iframe styles -----------------------------------------------------

const iframeWrapStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  border: "1px solid var(--line)",
  borderRadius: 6,
  overflow: "hidden",
  background: "var(--bg-1)",
  flex: 1,
  minHeight: 480,
};

const iframeBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "8px 12px",
  background: "rgba(11,14,22,0.85)",
  borderBottom: "1px solid var(--line)",
  flexWrap: "wrap",
};

const iframeStyle: CSSProperties = {
  width: "100%",
  // Fill the iframe wrapper which fills the remaining main area. Using
  // height: 100% lets it adapt as the surrounding chrome resizes; the
  // wrapper itself has flex: 1 within the main column.
  flex: 1,
  minHeight: 0,
  border: 0,
  display: "block",
  background: "var(--bg-0)",
};

const footerStripStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  padding: "8px 16px",
  background: "rgba(7,9,15,0.92)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

const ghostButton: CSSProperties = {
  textDecoration: "none",
  padding: "8px 14px",
  background: "transparent",
  border: "1px solid var(--line)",
  borderRadius: 3,
  fontFamily: "var(--font-ui)",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 1.5,
  color: "var(--fg-2)",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const mobileMainGridStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "10px 12px 16px",
};

const mobileRailStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const mobileTileScrollStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 6,
  WebkitOverflowScrolling: "touch",
};

const mobileMainAreaStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  minWidth: 0,
};

const goldButton: CSSProperties = {
  textDecoration: "none",
  padding: "8px 14px",
  background:
    "linear-gradient(180deg, var(--gold-bright) 0%, var(--gold) 100%)",
  border: "1px solid var(--gold-deep)",
  borderRadius: 3,
  fontFamily: "var(--font-ui)",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 1.5,
  color: "var(--gold-shadow)",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};
